<template>
  <div class="container-fluid">
    <div class="row mb-5">
      <h1>{{ purchasesOrder.seq_number ?? purchasesOrder.code }}</h1>
    </div>

    <div class="row mb-5">
      <div class="col-12">
        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.CREATED_AT") }}</dt>
          <dd class="col-sm-8">
            {{ $timeZoneDateFormat(purchasesOrder.created_at) }}
          </dd>
        </dl>

        <!-- <dl class="row">
          <dt class="col-sm-4">{{ $t("PURCHASES_ORDERS.EXPIRATION_TIME") }}</dt>
          <dd class="col-sm-8">
            {{ $timeZoneDateFormat(purchasesOrder.expiration_time) }}
          </dd>
        </dl> -->

        <dl class="row" v-if="purchasesOrder.expected_delivery_date">
          <dt class="col-sm-4">{{ $t("COMMON.EXPECTED_DELIVERY_DATE") }}</dt>
          <dd class="col-sm-8">
            {{ $dateFormat(purchasesOrder.expected_delivery_date, 'LL') }}
          </dd>
        </dl>

        <dl class="row" v-if="purchasesOrder.validated_at">
          <dt class="col-sm-4">{{ $t("COMMON.VALIDATED_AT") }}</dt>
          <dd class="col-sm-8">
            {{ $timeZoneDateFormat(purchasesOrder.validated_at) }}
          </dd>
        </dl>

        <dl class="row" v-if="purchasesOrder.sentBy">
          <dt class="col-sm-4">{{ $t("PURCHASES_ORDERS.SENT_BY") }}</dt>
          <dd class="col-sm-8">
            {{ $objectDenomination(purchasesOrder.sentBy) }}
          </dd>
        </dl>

        <dl class="row" v-if="hasSentRecipients">
          <dt class="col-sm-4">{{ $t("PURCHASES_ORDERS.SENT_TO") }}</dt>
          <dd class="col-sm-8">
            <div v-if="sentToList.length">
              <strong>{{ $t("PURCHASES_ORDERS.TO") }}:</strong>
              <div>
                <span v-for="(item, idx) in sentToList" :key="'to-' + idx">
                  <a :href="`mailto:${typeof item === 'string' ? item : item.email}`">
                    {{ typeof item === "string" ? item : item.email }}</a
                  ><span v-if="idx !== sentToList.length - 1">, </span>
                </span>
              </div>
            </div>
            <div v-if="sentCcList.length" class="mt-2">
              <strong>{{ $t("PURCHASES_ORDERS.CC") }}:</strong>
              <div>
                <span v-for="(item, idx) in sentCcList" :key="'cc-' + idx">
                  <a :href="`mailto:${typeof item === 'string' ? item : item.email}`">
                    {{ typeof item === "string" ? item : item.email }}</a
                  ><span v-if="idx !== sentCcList.length - 1">, </span>
                </span>
              </div>
            </div>
            <div v-if="sentBccList.length" class="mt-2">
              <strong>{{ $t("PURCHASES_ORDERS.BCC") }}:</strong>
              <div>
                <span v-for="(item, idx) in sentBccList" :key="'bcc-' + idx">
                  <a :href="`mailto:${typeof item === 'string' ? item : item.email}`">
                    {{ typeof item === "string" ? item : item.email }}</a
                  ><span v-if="idx !== sentBccList.length - 1">, </span>
                </span>
              </div>
            </div>
          </dd>
        </dl>

        <dl class="row" v-if="purchasesOrder.sent_at">
          <dt class="col-sm-4">{{ $t("PURCHASES_ORDERS.SENT_AT") }}</dt>
          <dd class="col-sm-8">
            {{ $timeZoneDateFormat(purchasesOrder.sent_at) }}
          </dd>
        </dl>

        <dl
          class="row"
          v-if="$currentUserCan($permissions.PERM_VIEW_ANY_SUPPLY_RULES) && !!supplyRule"
        >
          <dt class="col-sm-4">{{ $t("COMMON.SUPPLY_RULE") }}</dt>
          <dd class="col-sm-8">
            <object-link :object="supplyRule" />
          </dd>
        </dl>
        
        <dl
          class="row"
          v-if="$currentUserCan($permissions.PERM_VIEW_ANY_CAFETERIAS)"
        >
          <dt class="col-sm-4">{{ $t("COMMON.CAFETERIA") }}</dt>
          <dd class="col-sm-8">
            <cafeteria :cafeteria="purchasesOrder.cafeteria" />
          </dd>
        </dl>

        <dl
          class="row"
          v-if="$currentUserCan($permissions.PERM_VIEW_ANY_ESTABLISHMENTS)"
        >
          <dt class="col-sm-4">{{ $t("COMMON.ESTABLISHMENT") }}</dt>
          <dd class="col-sm-8">
            <establishment :establishment="purchasesOrder.establishment" />
          </dd>
        </dl>

        <dl
          class="row"
          v-if="$currentUserCan($permissions.PERM_VIEW_ANY_SERVICECENTERS)"
        >
          <dt class="col-sm-4">{{ $t("COMMON.SERVICECENTER") }}</dt>
          <dd class="col-sm-8">
            <service-center :serviceCenter="purchasesOrder.serviceCenter" />
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.LOCATIONS") }}</dt>
          <dd class="col-sm-8">
            <locations :locations="purchasesOrder.allowedLocations" />
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.ORGANIZATION") }}</dt>
          <dd class="col-sm-8">
            <organization :organization="purchasesOrder.organization" />
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.STATUS") }}</dt>
          <dd class="col-sm-8">
            <purchases-order-status-badge
              :purchasesOrder="purchasesOrder"
              :advanced="true"
            />
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.HAS_TAXES") }}</dt>
          <dd class="col-sm-8">
            <icon-check :checked="!purchasesOrder.has_no_taxes" />
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.PAYMENT_TERMS") }}</dt>
          <dd class="col-sm-8">
            <object-link :object="purchasesOrder.paymentTerm" />
          </dd>
        </dl>

        <dl class="row" v-if="purchasesOrder.purchasesInvoice">
          <dt class="col-sm-4">{{ $t("COMMON.PURCHASES_INVOICE") }}</dt>
          <dd class="col-sm-8">
            <object-link :object="purchasesOrder.purchasesInvoice" />
            <purchases-invoice-status-badge
              :purchasesInvoice="purchasesOrder.purchasesInvoice"
            />
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.SUPPLIER") }}</dt>
          <dd class="col-sm-8">
            <object-link :object="purchasesOrder.issuer" />
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.WAREHOUSE") }}</dt>
          <dd class="col-sm-8">
            <object-link
              v-if="!!purchasesOrder.destinationWarehouse"
              :object="purchasesOrder.destinationWarehouse"
            />
            <span v-if="!purchasesOrder.destinationWarehouse">
              {{ $t("COMMON.NONE") }}
            </span>
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.TOTAL") }}</dt>
          <dd class="col-sm-8" v-if="purchasesOrder.pricing">
            <span> {{ $formatCurrency(purchasesOrder.pricing.total) }} </span>
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4" v-if="purchasesOrder.excerpt">
            {{ $t("COMMON.NOTE") }}
          </dt>
          <dd class="col-sm-8" v-if="purchasesOrder.excerpt">
            <div v-html="purchasesOrder.excerpt"></div>
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4" v-if="purchasesOrder.terms_conditions">
            {{ $t("COMMON.TERMS_CONDITIONS") }}
          </dt>
          <dd class="col-sm-8" v-if="purchasesOrder.terms_conditions">
            <div v-html="purchasesOrder.terms_conditions"></div>
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4" v-if="purchasesOrder.description">
            {{ $t("COMMON.DESCRIPTION") }}
          </dt>
          <dd class="col-sm-8" v-if="purchasesOrder.description">
            <div v-html="purchasesOrder.description"></div>
          </dd>
        </dl>
      </div>
    </div>
  </div>
</template>

<script>
import Cafeteria from "@/components/Cafeteria.vue";
import Establishment from "@/components/Establishment.vue";
import ServiceCenter from "@/components/ServiceCenter.vue";
import IconCheck from "@/components/IconCheck.vue";
import PurchasesInvoiceStatusBadge from "../../PurchasesInvoiceManagement/partials/PurchasesInvoiceStatusBadge.vue";
import PurchasesOrderStatusBadge from "./PurchasesOrderStatusBadge.vue";
import ObjectLink from '../../../../../components/ObjectLink.vue';

export default {
  name: "purchasesOrder-view-global",

  components: {
    PurchasesOrderStatusBadge,
    PurchasesInvoiceStatusBadge,
    Cafeteria,
    Establishment,
    ServiceCenter,
    IconCheck,
    ObjectLink,
  },

  props: ["purchasesOrder", "supplyRule"],

  data() {
    return {};
  },

  computed: {
    normalizedSentTo() {
      const sentTo = this.purchasesOrder && this.purchasesOrder.sent_to;
      if (!sentTo) {
        return { to: [], cc: [], bcc: [] };
      }
      if (typeof sentTo === "string" || Array.isArray(sentTo)) {
        return {
          to: this.normalizeRecipients(sentTo),
          cc: [],
          bcc: [],
        };
      }
      return {
        to: this.normalizeRecipients(sentTo.to),
        cc: this.normalizeRecipients(sentTo.cc),
        bcc: this.normalizeRecipients(sentTo.bcc),
      };
    },
    sentToList() {
      return this.normalizedSentTo.to;
    },
    sentCcList() {
      return this.normalizedSentTo.cc;
    },
    sentBccList() {
      return this.normalizedSentTo.bcc;
    },
    hasSentRecipients() {
      return (
        this.sentToList.length ||
        this.sentCcList.length ||
        this.sentBccList.length
      );
    },
  },

  created() {},

  methods: {
    normalizeRecipients(value) {
      if (!value) {
        return [];
      }
      return Array.isArray(value) ? value : [value];
    },
  },

  mounted() {},

  watch: {
    purchasesOrder(purchasesOrder) {},
  },
};
</script>
